import React from 'react';
import moment from 'moment';
import 'components/order/detail/PrintOrder.css';
import { formatMoney, numberToWords } from '@app/utils/utils';
import { OrderModel } from '@app/interfaces/order';
import { PhoneFilled } from '@ant-design/icons';
import { CiGlobe, CiLocationOn } from 'react-icons/ci';
import { DATE_FORMAT } from '@app/constants/dateFormats';

interface OrderProps {
  record: OrderModel;
  customer: any;
  logoUrl?: string;
  storeName?: string;
  storePhone?: string;
  storeAddress?: string;
  storeWebsite?: string;
}

const ItemTable: React.FC<{ record: OrderModel }> = ({ record }) => {
  const items = record.items || [];

  return (
    <>
      <table className="print-table">
        <colgroup>
          <col className="col-stt" />
          <col className="col-name" />
          <col className="col-unit" />
          <col className="col-qty" />
          <col className="col-price" />
          <col className="col-total" />
        </colgroup>
        <thead>
          <tr>
            <th className="text-center">STT</th>
            <th>Tên hàng</th>
            <th className="text-center">ĐVT</th>
            <th className="text-center">SL</th>
            <th className="text-right">Đơn giá</th>
            <th className="text-right">Thành tiền</th>
          </tr>
        </thead>
        <tbody>
          {items.length > 0 ? (
            items.map((item: any, index: number) => (
              <tr key={index}>
                <td className="text-center">{index + 1}</td>
                <td>{item.name}</td>
                <td className="text-center">{item.unit}</td>
                <td className="text-center">{item.qty}</td>
                <td className="text-right">{formatMoney(Number(item.price))}</td>
                <td className="text-right">{formatMoney(Number(item.amount))}</td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan={6} className="print-table__empty-cell">
                Không có sản phẩm
              </td>
            </tr>
          )}
        </tbody>
      </table>

      <div className="invoice-summary">
        <div className="invoice-summary__row">
          <span className="invoice-summary__label">Tổng giá trị đơn hàng</span>
          <span className="invoice-summary__value">{formatMoney(record.before_discount_amount)}</span>
        </div>
        <div className="invoice-summary__row">
          <span className="invoice-summary__label">Phí trả hàng</span>
          <span className="invoice-summary__value">{formatMoney(record.shipping_fee ?? 0)}</span>
        </div>
        <div className="invoice-summary__row invoice-summary__row--total">
          <span className="invoice-summary__label">TIỀN TRẢ KHÁCH</span>
          <span className="invoice-summary__value">{formatMoney(record.total_amount)}</span>
        </div>
      </div>

      <div className="amount-words-line">
        Bằng chữ: <strong>{numberToWords(record.total_amount)}.</strong>
      </div>
    </>
  );
};

const PrintOrderReturnTemp: React.FC<OrderProps> = ({
  record,
  customer,
  logoUrl = '',
  storeName = '',
  storePhone = '',
  storeAddress = '',
  storeWebsite = '',
}) => {
  const now = new Date();
  const { day, month, year } = {
    day: now.getDate(),
    month: now.getMonth() + 1,
    year: now.getFullYear(),
  };

  return (
    <div className="container">
      {logoUrl && <img src={logoUrl} alt="" className="invoice-watermark" aria-hidden="true" />}

      <div className="store-header">
        {logoUrl && (
          <div className="store-header__brand">
            <img src={logoUrl} alt={storeName} className="store-header__logo" />
          </div>
        )}
        <div className="store-header__info">
          <div className="store-header__name">{storeName}</div>
          {storeAddress && (
            <div className="store-header__row"><CiLocationOn /> {storeAddress}</div>
          )}
          {storePhone && (
            <div className="store-header__row"><PhoneFilled /> {storePhone}</div>
          )}
          {storeWebsite && (
            <div className="store-header__row"><CiGlobe /> {storeWebsite}</div>
          )}
        </div>
      </div>

      <div className="voucher-title">
        <h1>Hoá đơn trả hàng tạm tính</h1>
      </div>
      <div className="voucher-meta">
        <span>Số: <strong>Trả hàng từ {record.order_code}</strong></span>
        <span>Ngày: <strong>{moment(record.trans_date).format(DATE_FORMAT)}</strong></span>
      </div>

      <div className="field-row">
        <span className="label">Khách hàng</span>
        <span className="value">{customer?.name}</span>
      </div>
      <div className="field-row">
        <span className="label">Địa chỉ</span>
        <span className="value">{customer?.address}</span>
      </div>
      <div className="field-row">
        <span className="label">Người bán</span>
        <span className="value">{record.seller_by}</span>
      </div>
      {record.remark && (
        <div className="field-row">
          <span className="label">Ghi chú</span>
          <span className="value text-danger">{record.remark}</span>
        </div>
      )}

      <ItemTable record={record} />

      <div className="signature">
        <div className="signature-block">
          <div className="signature-block__title">KHÁCH HÀNG</div>
          <div className="signature-block__note">(Ký, ghi rõ họ tên)</div>
          <div className="signature-block__name"></div>
        </div>
        <div className="signature-block">
          <div className="signature-block__title">NGƯỜI LẬP HÓA ĐƠN</div>
          <div className="signature-block__note">
            Ngày {day} tháng {month} năm {year}
            <br />
            (Ký, ghi rõ họ tên)
          </div>
          <div className="signature-block__name"></div>
        </div>
      </div>
    </div>
  );
};

export default PrintOrderReturnTemp;
